// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { SegmentedControl, type SegmentedControlItem, Stack } from '@mantine/core';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import {
  selectableViews,
  type SelectedView,
  useChangeDataView,
} from 'components/Mantine/ChangeDataView/useChangeDataView';
import { isNil } from 'helpers/isNotNil';

const tabIcon: Record<SelectedView, EvolveIconName> = {
  chart: 'BarChart',
  list: 'List',
  table: 'TableChart',
  kanban: 'Kanban',
  calendar: 'CalendarBoard',
};

const labels: Record<SelectedView, string> = {
  chart: 'Chart',
  list: 'List',
  table: 'Table',
  kanban: 'Kanban',
  calendar: 'Calendar',
};

export const ChangeDataView = () => {
  const context = useChangeDataView();

  const assembledData = useMemo(() => {
    const data: SegmentedControlItem[] = [];
    selectableViews.forEach((v) => {
      if (context?.viewsToShow.includes(v)) {
        data.push({
          value: v,
          label: (
            <Stack gap={0} align="center" my={5}>
              <EvolveIcon icon={tabIcon[v]} />
              {labels[v]}
            </Stack>
          ),
        });
      }
    });
    return data;
  }, [context?.viewsToShow]);

  if (isNil(context) || assembledData.length === 0) return null;

  const { selectedView, setSelectedView } = context;

  return (
    <SegmentedControl
      size="xs"
      onChange={(v) => setSelectedView(v as SelectedView)}
      value={selectedView}
      data={assembledData}
      withItemsBorders={false}
    />
  );
};
